import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNotificationContext } from '@/Context/NotificationContext';
import { TestTypes } from '@/Models/Configuration';
import UIStrings from '@/Utils/UIStrings';
import { useCreateRecruitmentContext } from '../../Create/Contexts/CreateRecruitmentContext';
import StepProps from '../StepProps';
import SelectTestsStepHeader from './Components/StepHeader/SelectTestsStepHeader';
import EstimatedTime from './Components/StepperNavigationButtons/Components/EstimatedTime';
import NextButtonComponent from './Components/StepperNavigationButtons/Components/NextButtonComponent';
import { SelectTestsStepperNavigationButtons } from './Components/StepperNavigationButtons/SelectTestsStepperNavigationButtons';
import TestsPanel from './Components/TestsPanel/TestsPanel';

export interface TestSelectionFormValues {
    isAptitudeSelected: boolean;
    isEmotionalIntelligenceSelected: boolean;
    isSkillsSelected: boolean;
    isPersonalitySelected: boolean;
    testLanguageField: string;
}

const SelectTestsStep = ({ activeStep, handleNext, handleBack }: StepProps): React.JSX.Element => {
    const { includedTests, setIncludedTests, testLanguage, setTestLanguage, processingState } =
        useCreateRecruitmentContext();

    const hasTest = (test: TestTypes): boolean => includedTests.indexOf(test) > -1;
    const { showFailToaster } = useNotificationContext();

    const form = useForm({
        defaultValues: {
            isAptitudeSelected: hasTest(TestTypes.Aptitude),
            isEmotionalIntelligenceSelected: hasTest(TestTypes.EmotionalIntelligence),
            isSkillsSelected: hasTest(TestTypes.Skills),
            isPersonalitySelected: hasTest(TestTypes.Personality),
            testLanguageField: testLanguage,
        },
    });

    const submitHandler = async (values: TestSelectionFormValues) => {
        const tests: TestTypes[] = [];
        if (values.isAptitudeSelected) {
            tests.push(TestTypes.Aptitude);
        }
        if (values.isEmotionalIntelligenceSelected) {
            tests.push(TestTypes.EmotionalIntelligence);
        }
        if (values.isPersonalitySelected) {
            tests.push(TestTypes.Personality);
        }
        if (values.isSkillsSelected) {
            tests.push(TestTypes.Skills);
        }

        if (tests.length === 0) {
            showFailToaster(UIStrings.PleaseSelectAtLeastOneTestToContinue);
            return;
        }

        setIncludedTests(tests);
        setTestLanguage(values.testLanguageField);

        if (handleNext) {
            handleNext();
        }
    };

    return (
        <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(submitHandler)}>
                <SelectTestsStepHeader />
                <TestsPanel />
                <SelectTestsStepperNavigationButtons
                    activeStep={activeStep}
                    handleBack={handleBack}
                    nextButtonComponent={<NextButtonComponent processingState={processingState} />}
                    centeredComponent={<EstimatedTime />}
                />
            </form>
        </FormProvider>
    );
};

export default SelectTestsStep;
